/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-unstable-nested-components */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import FiltersButtonArray from "components/company/v3/FiltersButtonArray";
import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import { Button } from "components/ui/Button";
import { ScrollArea } from "components/ui/ScrollArea";
import TablePagination from "components/ui/Table/TablePagination";
import classNames from "components/ui/classNames";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import orderBy from "lodash/orderBy";
import { useEffect, useState } from "react";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import { IBrazilianCompanyIssuerShareholderType, type IBrazilianCompanyIssuerTreasuryShares } from "types/graphqlTypes";

enum ShareholderType {
  Corporation = "CORPORATION",
  Individual = "INDIVIDUAL",
  Other = "OTHER",
  Treasury = "TREASURY",
  Totalizer = "TOTALIZER",
  MinorityGroup = "MINORITY_GROUP",
}

type PositionOfShareholdersQueryType = IBrazilianCompanyIssuerTopLevelFragment["positionOfShareholders"]["0"];
type PositionOfShareholders = Omit<PositionOfShareholdersQueryType, "__typename" | "type"> & {
  type: ShareholderType;
};

interface BrazilianGovernanceContentProps {
  isRenderPageDefault: boolean;
}

export default function BrazilianGovernanceContent({ isRenderPageDefault }: BrazilianGovernanceContentProps) {
  const [filter, setFilter] = useState<string>("GROUPED_LIST");

  const intl = useIntl();
  const issuer = useBrazilianIssuer();
  const { positionOfShareholders, treasuryShares } = issuer;

  const { shareholders, counts } = getShareHolders(issuer, positionOfShareholders, treasuryShares, filter, intl);

  const hasShareholdersGrouped = counts.grouped > 0;

  useEffect(() => {
    if (!hasShareholdersGrouped) {
      setFilter("COMPLETE_LIST");
    }
  }, [hasShareholdersGrouped]);

  return (
    <div className="flex h-full w-full flex-col gap-y-4">
      <FiltersButtonArray
        filters={[
          {
            key: "GROUPED_LIST",
            label: intl.formatMessage(
              {
                defaultMessage: "Lista Agrupada ({count})",
                id: "cSnWpP",
              },
              {
                count: counts.grouped,
              },
            ),
            hidden: !hasShareholdersGrouped,
          },
          {
            key: "COMPLETE_LIST",
            label: intl.formatMessage(
              {
                defaultMessage: "Lista Completa ({count})",
                id: "1L1FAY",
              },
              {
                count: counts.total,
              },
            ),
          },
          {
            key: "CONTROLLERS",
            label: intl.formatMessage(
              {
                defaultMessage: "Controladores ({count})",
                id: "xHQGwN",
              },
              {
                count: counts.controllers,
              },
            ),
          },
          {
            key: "GOVERNMENT",
            label: intl.formatMessage(
              {
                defaultMessage: "Governo ({count})",
                id: "bdNZ5b",
              },
              {
                count: counts.government,
              },
            ),
            disabled: !positionOfShareholders.some((node) => node.isGovernmentOrGovernmentRelated),
          },
        ]}
        filter={filter}
        setFilter={setFilter}
      />

      <ShareholdersTable
        shareholders={shareholders}
        isRenderPageDefault={isRenderPageDefault}
        filter={filter}
        hasShareholdersGrouped={hasShareholdersGrouped}
        onResetFilter={() => {
          setFilter("GROUPED_LIST");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    </div>
  );
}
interface ShareholdersTableProps {
  shareholders: PositionOfShareholders[];
  isRenderPageDefault: boolean;
  filter: string;
  hasShareholdersGrouped: boolean;
  onResetFilter: () => void;
}

function ShareholdersTable({
  shareholders,
  isRenderPageDefault,
  filter,
  hasShareholdersGrouped,
  onResetFilter,
}: ShareholdersTableProps) {
  const intl = useIntl();
  const columnHelper = createColumnHelper<PositionOfShareholders>();

  const hasShareholderWithPreferredStock = shareholders.some((shareholder) => {
    return shareholder.percentageOfTotalSharesOfPreferredStock > 0;
  });

  const columns = [
    columnHelper.accessor("name", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-left text-sm font-semibold text-slate-600 dark:text-slate-300">
          <FormattedMessage defaultMessage="Nome" id="CH/6DN" />
        </div>
      ),
      cell: (info) => (
        <div className={classNames("px-4 py-1.5 text-sm font-normal text-slate-600 dark:text-slate-400")}>
          <div className="flex items-center">
            <div className="w-full max-w-sm overflow-hidden text-ellipsis whitespace-nowrap text-left lg:max-w-full">
              <span
                className={classNames("text-sm font-medium text-[#394e66] dark:text-slate-200", {
                  "font-bold": info.row.original.type === ShareholderType.Totalizer,
                  "text-blue-600 dark:text-blue-400": info.row.original.type === ShareholderType.MinorityGroup,
                })}
              >
                {info.getValue()}
              </span>
            </div>
            {info.row.original.isGovernmentOrGovernmentRelated && (
              <div
                className={classNames(
                  "whitespace-nowrap",
                  "px-2 py-1 text-xs font-normal",
                  "text-slate-600 dark:text-slate-400",
                )}
              >
                <span className="rounded-xl bg-red-100 px-3 py-1 font-medium tracking-wider text-red-600 dark:bg-red-900/30 dark:text-red-400">
                  <FormattedMessage defaultMessage="Governo" id="Tefd0D" />
                </span>
              </div>
            )}
          </div>
        </div>
      ),
    }),
    columnHelper.accessor("isControllingShareholder", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300">
          Controlador
        </div>
      ),
      cell: (info) => {
        const isHypen = [
          ShareholderType.Totalizer,
          ShareholderType.Other,
          ShareholderType.Treasury,
          ShareholderType.MinorityGroup,
        ].includes(info.row.original.type);

        if (isHypen) {
          return (
            <div
              className={classNames(
                "whitespace-nowrap px-4 py-1.5 text-sm font-normal text-slate-600 dark:text-slate-400",
              )}
            >
              -
            </div>
          );
        }

        return (
          <div
            className={classNames("px-4 py-1.5 text-sm font-medium tracking-wide text-blue-600 dark:text-blue-400", {
              "text-red-600 dark:text-red-400": !info.getValue(),
            })}
          >
            {info.getValue()
              ? intl.formatMessage({
                  defaultMessage: "Sim",
                  id: "FTkqVk",
                })
              : intl.formatMessage({
                  defaultMessage: "Não",
                  id: "mggv3Q",
                })}
          </div>
        );
      },
    }),
    columnHelper.accessor("percentageOfTotalSharesOfCommonStock", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300">
          % ON
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames("whitespace-nowrap px-4 py-1.5 text-sm font-medium text-green-600 dark:text-green-400")}
        >
          {info.getValue() < 0.01
            ? info.getValue().toFixed(3).replace(".000", "").replace(".", ",")
            : info.getValue().toFixed(2).replace(".00", "").replace(".", ",")}
          %
        </div>
      ),
    }),
    columnHelper.accessor("percentageOfTotalSharesOfPreferredStock", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300">
          % PN
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap px-4 py-1.5 text-sm font-medium text-yellow-700 dark:text-yellow-500",
          )}
        >
          {info.getValue() < 0.01
            ? info.getValue().toFixed(3).replace(".000", "").replace(".", ",")
            : info.getValue().toFixed(2).replace(".00", "").replace(".", ",")}
          %
        </div>
      ),
      meta: {
        isHidden: !hasShareholderWithPreferredStock,
      },
    }),
  ];

  const table = useReactTable({
    data: shareholders,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: !isRenderPageDefault,
    initialState: {
      pagination: {
        pageSize: 12,
      },
    },
  });

  return (
    <div className="flex h-auto w-full flex-col items-end space-y-2">
      <ScrollArea type="auto" orientation="horizontal" className="relative h-full w-full">
        <div className="lg:w-full">
          <div
            className={classNames("relative overflow-auto rounded-xl border border-slate-200", "dark:border-slate-950")}
          >
            <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                  >
                    {headerGroup.headers
                      .filter((header) => !header.column.columnDef.meta?.isHidden)
                      .map((header) => {
                        return (
                          <th
                            key={header.id}
                            className={classNames(
                              "m-0 bg-slate-50 p-0 text-center dark:bg-slate-800",
                              header.column.columnDef.meta?.header?.className,
                            )}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        );
                      })}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {table.getRowModel().rows.map((row) => {
                  return (
                    <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                      {row
                        .getVisibleCells()
                        .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                        .map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              className={classNames("m-0 p-0 text-center", cell.column.columnDef.meta?.cell?.className)}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </ScrollArea>

      {isRenderPageDefault && <TablePagination<PositionOfShareholders> table={table} />}

      {!isRenderPageDefault && hasShareholdersGrouped && filter === "COMPLETE_LIST" && (
        <Button variant="link" onClick={onResetFilter} className="mx-auto">
          <FormattedMessage defaultMessage="Agrupar novamente" id="SJr9tl" />
        </Button>
      )}
    </div>
  );
}

function getShareHolders(
  issuer: IBrazilianCompanyIssuerTopLevelFragment,
  positionOfShareholders: IBrazilianCompanyIssuerTopLevelFragment["positionOfShareholders"],
  treasuryShares: IBrazilianCompanyIssuerTreasuryShares | null,
  filter: string,
  intl: IntlShape,
) {
  const filteredShareholders = positionOfShareholders
    .filter((node) => {
      if (filter === "GROUPED_LIST") {
        return node.percentageOfTotalSharesOfCommonStock > 5;
      }

      if (filter === "CONTROLLERS") {
        return node.isControllingShareholder;
      }

      if (filter === "GOVERNMENT") {
        return node.isGovernmentOrGovernmentRelated;
      }

      return true;
    })
    .map(
      (node) =>
        ({
          ...node,
          type:
            node.type === IBrazilianCompanyIssuerShareholderType.Corporation
              ? ShareholderType.Corporation
              : node.type === IBrazilianCompanyIssuerShareholderType.Individual
                ? ShareholderType.Individual
                : ShareholderType.Other,
          __typename: "BrazilianCompanyIssuerShareholderPosition",
        }) as PositionOfShareholders,
    );

  const shareholders = orderBy(filteredShareholders, ["percentageOfTotalSharesOfCommonStock"], ["desc"]);

  const groupedShareholders = positionOfShareholders.filter((node) => node.percentageOfTotalSharesOfCommonStock <= 5);

  if (filter === "GROUPED_LIST" && groupedShareholders.length > 0) {
    shareholders.push({
      isControllingShareholder: false,
      isGovernmentOrGovernmentRelated: false,
      name: intl.formatMessage({
        defaultMessage: "*Minoritários Agrupados (ON < 5%)",
        id: "vadjVJ",
      }),
      nationality: "",
      percentageOfTotalShares: groupedShareholders.reduce((total, item) => total + item.percentageOfTotalShares, 0),
      percentageOfTotalSharesOfCommonStock: groupedShareholders.reduce(
        (total, item) => total + item.percentageOfTotalSharesOfCommonStock,
        0,
      ),
      percentageOfTotalSharesOfPreferredStock: groupedShareholders.reduce(
        (total, item) => total + item.percentageOfTotalSharesOfPreferredStock,
        0,
      ),
      totalSharesOfCommonStock: groupedShareholders.reduce((total, item) => total + item.totalSharesOfCommonStock, 0),
      totalShares: groupedShareholders.reduce((total, item) => total + item.totalShares, 0),
      totalSharesOfPreferredStock: groupedShareholders.reduce(
        (total, item) => total + item.totalSharesOfPreferredStock,
        0,
      ),
      type: ShareholderType.MinorityGroup,
    });
  }

  if (filter !== "CONTROLLERS" && filter !== "GOVERNMENT") {
    const hasOthersShareholders = issuer.freeFloat && issuer.freeFloat.percentageOfTotalShares > 0;
    const hasTreasuryShares = treasuryShares && treasuryShares.percentageOfTotalShares > 0;

    if (hasOthersShareholders && issuer.freeFloat) {
      shareholders.push({
        isControllingShareholder: false,
        isGovernmentOrGovernmentRelated: false,
        name: intl.formatMessage({
          defaultMessage: "Outros",
          id: "N7uKhl",
        }),
        nationality: "",
        percentageOfTotalShares: issuer.freeFloat.percentageOfTotalShares,
        percentageOfTotalSharesOfCommonStock: issuer.freeFloat.percentageOfTotalSharesOfCommonStock,
        percentageOfTotalSharesOfPreferredStock: issuer.freeFloat.percentageOfTotalSharesOfPreferredStock,
        totalSharesOfCommonStock: 0,
        totalShares: issuer.freeFloat.totalShares,
        totalSharesOfPreferredStock: issuer.freeFloat.totalSharesOfPreferredStock,
        type: ShareholderType.Other,
      });
    }

    if (hasTreasuryShares) {
      shareholders.push({
        isControllingShareholder: false,
        isGovernmentOrGovernmentRelated: false,
        name: intl.formatMessage({
          defaultMessage: "Tesouraria",
          id: "V2ZKAP",
        }),
        nationality: "",
        percentageOfTotalShares: treasuryShares.percentageOfTotalShares,
        percentageOfTotalSharesOfCommonStock: treasuryShares.percentageOfTotalSharesOfCommonStock,
        percentageOfTotalSharesOfPreferredStock: treasuryShares.percentageOfTotalSharesOfPreferredStock,
        totalSharesOfCommonStock: treasuryShares.totalSharesOfCommonStock,
        totalShares: treasuryShares.totalShares,
        totalSharesOfPreferredStock: treasuryShares.totalSharesOfPreferredStock,
        type: ShareholderType.Treasury,
      });
    }
  }

  return {
    shareholders: [
      ...shareholders,
      {
        isControllingShareholder: false,
        isGovernmentOrGovernmentRelated: false,
        name: intl.formatMessage({
          defaultMessage: "Total",
          id: "MJ2jZQ",
        }),
        nationality: "",
        percentageOfTotalShares: shareholders.reduce((total, item) => total + item.percentageOfTotalShares, 0),
        percentageOfTotalSharesOfCommonStock: shareholders.reduce(
          (total, item) => total + item.percentageOfTotalSharesOfCommonStock,
          0,
        ),
        percentageOfTotalSharesOfPreferredStock: shareholders.reduce(
          (total, item) => total + item.percentageOfTotalSharesOfPreferredStock,
          0,
        ),
        totalSharesOfCommonStock: shareholders.reduce((total, item) => total + item.totalSharesOfCommonStock, 0),
        totalShares: shareholders.reduce((total, item) => total + item.totalShares, 0),
        totalSharesOfPreferredStock: shareholders.reduce((total, item) => total + item.totalSharesOfPreferredStock, 0),
        type: ShareholderType.Totalizer,
      },
    ],
    counts: {
      total: positionOfShareholders.length,
      // If the list of grouped minority directors is greater than 0,
      // count the majority directors plus 1, which will represent the grouped directors.
      grouped:
        groupedShareholders.length > 0
          ? positionOfShareholders.filter((node) => node.percentageOfTotalSharesOfCommonStock > 5).length + 1
          : 0,
      controllers: positionOfShareholders.filter((node) => node.isControllingShareholder).length,
      government: positionOfShareholders.filter((node) => node.isGovernmentOrGovernmentRelated).length,
    },
  };
}
